/**
 * Onboarding: profile → add to home screen → notifications → consent.
 *
 * The server owns the order (lib/Onboarding.php) and re-checks each step's
 * precondition, so this screen cannot be skipped by poking the API.
 */

import { apiPost, ApiError } from '../js/api.js'
import { icon } from '../js/icons.js'
import { text } from '../js/html.js'
import { platform, canPromptInstall, promptInstall } from '../js/platform.js'
import { enablePush, permission as pushPermission } from '../js/push.js'
import { PRIVACY_SUMMARY, PRIVACY_VERSION } from '../content/privacy.js'

const TITLES = {
  profile: 'Who you are',
  install: 'Put it on your home screen',
  push: 'Notifications',
  consent: 'Privacy',
}

export function renderOnboarding(root, { user, onboarding, onDone, onUpdate }) {
  let state = { user: { ...user }, onboarding: { ...onboarding } }
  let busy = false
  let error = null
  // Only a step change animates; a redraw for a disabled button must not
  // replay the fade over the whole screen.
  let animatedStep = null
  let subscribed = false

  function draw() {
    const { step, index, steps } = state.onboarding
    const entering = step !== animatedStep
    animatedStep = step

    root.innerHTML = `
      <div class="onboarding ${entering ? 'onboarding--enter' : ''}">
        <ol class="stepper" aria-label="Setup steps">
          ${steps
            .map(
              (name, i) => `
            <li class="stepper__dot ${i < index ? 'stepper__dot--done' : ''}
                       ${i === index ? 'stepper__dot--active' : ''}">
              <span class="visually-hidden">${TITLES[name]}</span>
            </li>`,
            )
            .join('')}
        </ol>

        <h1 class="hero-title">${TITLES[step]}</h1>
        ${bodyFor(step)}
        ${error ? `<p class="notice notice--error">${text(error)}</p>` : ''}
      </div>
    `

    wire(step)
  }

  function bodyFor(step) {
    if (step === 'profile') return profileStep(state.user)
    if (step === 'install') return installStep()
    if (step === 'push') return pushStep()

    return consentStep()
  }

  // ---- step bodies -----------------------------------------------------

  function profileStep(current) {
    return `
      <p class="lede">A photo and a few sentences. The others see this before departure.</p>

      <section class="card">
        <label class="photo-picker">
          ${
            current.photo_path
              ? `<img class="photo-picker__image" src="/api/uploads/${text(current.photo_path)}" alt="Your photo" />`
              : `<span class="photo-picker__placeholder">${icon('camera', { size: 24 })}</span>`
          }
          <span class="photo-picker__text">
            ${current.photo_path ? 'Change the photo' : 'Add a photo'}
          </span>
          <input type="file" accept="image/*" id="photo" class="visually-hidden" />
        </label>

        <label class="field">
          <span class="field__label">O tebi</span>
          <textarea class="field__input" id="bio" rows="4" maxlength="2000"
            placeholder="Three sentences are enough.">${text(current.bio)}</textarea>
        </label>

        <button class="button button--primary" id="next" ${busy ? 'disabled' : ''}>
          ${busy ? 'Wait…' : 'Continue'}
        </button>
      </section>
    `
  }

  function installStep() {
    const where = platform()

    if (where === 'standalone') {
      return `
        <p class="lede">The app is already running from your home screen. That was all it needed.</p>
        <section class="card">
          <p class="success-line">${icon('check', { size: 20 })}<span>Dodana je.</span></p>
          ${nextButton('Continue')}
        </section>
      `
    }

    if (where === 'ios') {
      return `
        <p class="lede">
          On iPhone this is done from Safari. Two seconds, and the app has its own icon.
        </p>
        <section class="card">
          ${iosIllustration()}
          <ol class="steps">
            <li>In Safari, tap ${icon('share', { size: 16, className: 'icon--inline' })} <strong>Share</strong>.</li>
            <li>Scroll to <strong>Add to Home Screen</strong>.</li>
            <li>Confirm with <strong>Add</strong>.</li>
          </ol>
          <p class="hint">Then open it from the home screen — there it runs full screen.</p>
          ${nextButton('I added it')}
        </section>
      `
    }

    if (where === 'android') {
      return `
        <p class="lede">Jedan dodir i aplikacija dobije ikonu kao svaka druga.</p>
        <section class="card">
          ${
            canPromptInstall()
              ? `<button class="button button--primary" id="install">
                   ${icon('squarePlus', { size: 18 })}<span>Add to home screen</span>
                 </button>`
              : `<ol class="steps">
                   <li>Open the browser menu (three dots, top right).</li>
                   <li>Choose <strong>Install app</strong> or <strong>Add to Home screen</strong>.</li>
                 </ol>`
          }
          ${nextButton('Continue')}
        </section>
      `
    }

    return `
      <p class="lede">
        This is a phone app — that is where notifications arrive. Open it on your
        phone and add it to the home screen.
      </p>
      <section class="card">
        ${
          canPromptInstall()
            ? `<button class="button button--primary" id="install">
                 ${icon('squarePlus', { size: 18 })}<span>Install</span>
               </button>`
            : ''
        }
        ${nextButton('Continue')}
      </section>
    `
  }

  function pushStep() {
    const denied = pushPermission() === 'denied'
    const granted = subscribed || pushPermission() === 'granted'

    return `
      <p class="lede">
        The whole programme arrives through notifications — brief by brief, at the
        right moment. Without them the app is an empty shell.
      </p>

      <section class="card">
        <p class="reason">${icon('bell', { size: 20 })}<span>
          We send nothing but your retreat. No ads, no reminders.
        </span></p>

        ${
          granted
            ? `<p class="success-line">${icon('check', { size: 20 })}<span>Notifications are on.</span></p>`
            : denied
              ? `<p class="notice notice--error">
                   Notifications are blocked in your browser settings. Turn them on
                   there and come back — or continue and sort it out later.
                 </p>`
              : `<button class="button button--primary" id="enable-push" ${busy ? 'disabled' : ''}>
                   ${icon('bell', { size: 18 })}<span>Turn notifications on</span>
                 </button>`
        }

        ${nextButton(granted ? 'Continue' : 'Continue without them')}
      </section>
    `
  }

  function consentStep() {
    return `
      <p class="lede">Short, and without legal language.</p>

      <section class="card">
        <p class="reason">${icon('shieldCheck', { size: 20 })}<span>What we keep, and what we do not do</span></p>
        <ul class="bullets">
          ${PRIVACY_SUMMARY.map((line) => `<li>${line}</li>`).join('')}
        </ul>

        <p class="hint">
          <a href="/privatnost" data-link>The full privacy policy</a>
        </p>

        <label class="checkbox">
          <input type="checkbox" id="agree" />
          <span>I have read this and I agree.</span>
        </label>

        <button class="button button--primary" id="next" ${busy ? 'disabled' : ''}>
          ${busy ? 'Wait…' : 'Finish'}
        </button>
      </section>

      <p class="hint hint--centred">
        The orb is your guide — and your compass. Tap it anywhere to move around,
        or swipe left and right.
      </p>
    `
  }

  function nextButton(label) {
    return `<button class="button button--ghost" id="next" ${busy ? 'disabled' : ''}>${label}</button>`
  }

  // ---- behaviour -------------------------------------------------------

  function wire(step) {
    root.querySelector('#next')?.addEventListener('click', () => submit(step))
    root.querySelector('#photo')?.addEventListener('change', uploadPhoto)
    root.querySelector('#install')?.addEventListener('click', runInstallPrompt)
    root.querySelector('#enable-push')?.addEventListener('click', requestPush)
  }

  async function run(work) {
    if (busy) return

    busy = true
    error = null
    draw()

    try {
      await work()
    } catch (problem) {
      error = problem instanceof ApiError ? problem.message : 'Something went wrong.'
    } finally {
      busy = false
      draw()
    }
  }

  function uploadPhoto(event) {
    const file = event.target.files?.[0]

    if (file === undefined) return

    run(async () => {
      const form = new FormData()
      form.append('photo', file)

      const result = await apiPost('/me/photo', form)
      state.user.photo_path = result.photo_path
      onUpdate?.(state.user)
    })
  }

  async function runInstallPrompt() {
    await promptInstall()
    draw()
  }

  function requestPush() {
    run(async () => {
      const outcome = await enablePush()

      if (outcome === 'subscribed') {
        subscribed = true

        return
      }

      error = {
        denied: 'Notifications were refused. You can turn them on later in settings.',
        unsupported: 'This browser does not support notifications. We will email you instead.',
        unavailable: 'Notifications are not set up on the server yet. We will email you instead.',
      }[outcome]
    })
  }

  function submit(step) {
    // Read the form before run() redraws it — the redraw replaces these nodes.
    const bio = root.querySelector('#bio')?.value.trim() ?? ''
    const agreed = root.querySelector('#agree')?.checked ?? false

    if (step === 'consent' && !agreed) {
      error = 'Potrebna je privola za nastavak.'
      draw()

      return
    }

    run(async () => {
      if (step === 'profile') {
        await apiPost('/me/profile', { bio })
        state.user.bio = bio
      }

      if (step === 'consent') {
        await apiPost('/me/consent', {
          kind: 'privacy',
          granted: true,
          text_version: PRIVACY_VERSION,
        })
      }

      const result = await apiPost('/me/onboarding/advance', { from: step })
      state.onboarding = result.onboarding

      if (state.onboarding.complete) {
        onDone()
      }
    })
  }

  draw()
}

/** A small Safari toolbar mock so the share icon is unmistakable. */
function iosIllustration() {
  return `
    <figure class="ios-guide" role="img"
            aria-label="Donja traka Safarija s istaknutom ikonom Podijeli">
      <div class="ios-guide__bar">
        <span class="ios-guide__glyph">‹</span>
        <span class="ios-guide__glyph">›</span>
        <span class="ios-guide__share">${icon('share', { size: 20 })}</span>
        <span class="ios-guide__glyph">⌸</span>
        <span class="ios-guide__glyph">⧉</span>
      </div>
      <span class="ios-guide__pointer" aria-hidden="true"></span>
    </figure>
  `
}
